import { useCallback, useState } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { shareCopy } from '#src/app/shared/share/copy';

import { getShareUrl } from '../../../../../GrowPetition/components/ShareYourPetition/components/ShareUrl/utils';
import type { Props as MainProps } from '../../../../index';

import { useShortUrls } from './useShortUrls';

const DEFAULT_TRACKING_DATA = { context: 'petition_dashboard_group_rec' };

const DEFAULT_TRACKING_EVENTS = { success: 'share_petition', failure: 'share_petition_failure' };

type UseShareUrlReturn = ModelHookResult<{ copied: boolean }, { handleShare: () => void }>;

export function useShareUrl(viewContext?: MainProps['viewContext']): UseShareUrlReturn {
	const { resolveUrl } = useUtilityContext();
	const {
		data: { petition, viewer },
	} = useStarterDashboardV2PageData();

	// gets displayed url
	const shortUrls = useShortUrls({ slug: petition.slug });
	// concatenate after resolving, since resolveUrl will encode the string if its unicode and we want to show the un-encoded string
	const vanityUrl = petition.starterVanityUrl ? `${resolveUrl('/')}${petition.starterVanityUrl}` : null;
	const shareUrl = getShareUrl(vanityUrl, shortUrls.copy);

	const utilityContext = useUtilityContext();
	const track = useTracking();

	const [copied, setCopied] = useState(false);
	const handleCopiedState = useCallback(() => {
		setCopied(true);
		setTimeout(() => {
			setCopied(false);
		}, 3000);
	}, []);

	const handleShare = useCallback(() => {
		const options = {
			trackingData: {
				...DEFAULT_TRACKING_DATA,
				petition_status: petition.status.toLowerCase(),
				own_petition: petition.user.id === viewer.user?.id,
				vanity_url: !!petition.starterVanityUrl,
				...(viewContext ? { tab_at_share: viewContext } : {}),
			},
			trackingEvents: DEFAULT_TRACKING_EVENTS,
		};

		handleCopiedState();
		void shareCopy(shareUrl.url, options, track, utilityContext.i18n);
	}, [
		handleCopiedState,
		petition.starterVanityUrl,
		petition.status,
		petition.user.id,
		shareUrl,
		track,
		utilityContext.i18n,
		viewer.user?.id,
		viewContext,
	]);

	return { data: { copied }, actions: { handleShare } };
}
